// Hook: useAIGeneration
// Purpose: Handles AI-assisted campaign generation. Submits a prompt (and optional
// source URL) to the server, updates form data with returned fields, marks
// AI-generated fields, optionally sets survey questions, closes the AI modal,
// and advances the step.
import { useState } from 'react';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';

export const useAIGeneration = (
  formData, 
  updateFormData, 
  setSurveyQuestions, 
  setAiGeneratedFields, 
  setIsAIModalOpen, 
  setCurrentStep,
  namespaceId
) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  const handleAIGenerate = async (prompt, sourceUrl = '') => {
    // Use provided prompt or fallback to state
    const finalPrompt = prompt || aiPrompt;
    
    // Check if either prompt or sourceUrl is provided
    if (!finalPrompt.trim() && !sourceUrl.trim()) return;
    
    if (!namespaceId) {
      setAiError('No namespace selected. Please select a namespace before generating AI content.');
      return;
    }

    setIsGenerating(true);
    setAiError('');
    
    try {
      const idToken = await auth.currentUser.getIdToken();
      
      // Create an AbortController for the timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      console.log('generate-campaign: Setting timeout to 30 seconds for AI operation');
      
      const response = await fetch(`${SERVER_URL}/campaign/generate-campaign?namespaceId=${namespaceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          prompt: finalPrompt,
          sourceUrl: sourceUrl.trim() // Include the sourceUrl if provided
        }),
        signal: controller.signal
      });
      
      // Clear the timeout
      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.status === 'error') {
        setAiError(data.message || 'Failed to generate campaign');
        return;
      }

      // Update form data with AI generated content
      updateFormData({
        ...formData,
        title: data.campaignData.title || data.campaignData.name,
        description: data.campaignData.description,
        businessName: data.campaignData.businessName,
        website: data.campaignData.website || sourceUrl, // Use sourceUrl as fallback if not provided in response
        email: data.campaignData.email,
        phone: data.campaignData.phone,
      });

      // Set which fields were AI generated - only mark fields that actually have values
      const newAiGeneratedFields = {};
      
      // Only mark fields as AI-generated if they actually contain content
      if (data.campaignData.title || data.campaignData.name) {
        newAiGeneratedFields.title = true;
      }
      if (data.campaignData.description) newAiGeneratedFields.description = true;
      if (data.campaignData.businessName) newAiGeneratedFields.businessName = true;
      if (data.campaignData.website || sourceUrl) newAiGeneratedFields.website = true;
      if (data.campaignData.email) newAiGeneratedFields.email = true;
      if (data.campaignData.phone) newAiGeneratedFields.phone = true;
      
      setAiGeneratedFields(newAiGeneratedFields);
      
      // Set survey questions if provided
      if (data.campaignData.surveyQuestions && data.campaignData.surveyQuestions.length > 0) {
        // Filter out empty questions
        const validQuestions = data.campaignData.surveyQuestions
          .filter(question => question && question.trim() !== '')
          // Limit to a maximum of 3 questions
          .slice(0, 3);
        
        // Only proceed if we have at least one valid question
        if (validQuestions.length > 0) {
          const formattedQuestions = validQuestions.map((question, index) => ({
            id: index + 1,
            question: question
          }));
          
          setSurveyQuestions(formattedQuestions);
          
          // Mark survey questions as AI-generated
          formattedQuestions.forEach((_, index) => {
            newAiGeneratedFields[`question_${index}`] = true;
          });
          
          setAiGeneratedFields(newAiGeneratedFields);
        }
      }
      
      // Close the AI modal
      setIsAIModalOpen(false);
      
      // Go to the design step (step 2)
      setCurrentStep(2); // Always go to design step
    } catch (error) {
      console.error('Error generating campaign with AI:', error);
      
      // Handle timeout errors specifically
      if (error.name === 'AbortError') {
        setAiError('The AI is taking too long to respond. Please try again or use a simpler prompt.');
      } else {
        setAiError('Failed to generate campaign. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    aiPrompt,
    setAiPrompt,
    isGenerating,
    aiError,
    handleAIGenerate
  };
}; 